// R-FIN-PHASE-FEASIBILITY — Per-phase combined-mix feasibility check.
//
// Bridges the granular UI vocabulary (`PhasePowerComponentKey`, selected in
// the Project Intent form) to the financial technology presets
// (`TechnologyId`, with sourced capacity factors in finDecision/presets).
//
// The check only fires when a phase's power mix is declared as AND-combined
// (`powerSourceConfig.mixMode === 'and'`). In that mode the selected sources
// physically co-locate to serve ONE phase load, so we can ask: does the
// combined nameplate × capacity factor cover the phase's required MW?
//
// RESOURCE-ADEQUACY model. A datacenter is a FIRM 24/7 load, so the binding
// question is not "enough average energy" but "enough capacity we can count on
// at any moment, with a reserve". We grade the verdict on a firm-capacity tier
// and report an energy tier as context.
//
// CRITICAL DISTINCTION — capacity CREDIT (ELCC) ≠ capacity FACTOR:
//   - capacity FACTOR (CF) = long-run average output ÷ nameplate. Drives ENERGY
//     (annual MWh). A peaker's market CF is low because it rarely runs.
//   - capacity CREDIT (ELCC, "effective load-carrying capability") = the share
//     of nameplate you can RELY ON to serve firm load. For a firm dispatchable
//     unit it ≈ availability (~92%, far above a peaker's CF); for an intermittent
//     source it is FAR BELOW its CF (solar/wind can't be counted on at a given
//     moment), even paired with a few hours of storage on a flat 24/7 load.
// Using CF for the firm tier (the original build) understated firm dispatchable
// sources ~6× (recip→gas-sc CF 15%) and overstated intermittent firm value — so
// the firm tier uses DEFAULT_CAPACITY_CREDIT_PCT, NOT the capacity factor.
//
// Refs: TD World "Calculating Capacity Credits for Distributed Solar"; MISO Wind
// & Solar Capacity Credit Report; NREL capacity-credit work; Ascend Analytics /
// NERC resource adequacy (1-in-10 / 0.1 LOLE; 15–18% planning reserve margins).
//
// UNITS: MW throughout (nameplate), % for capacity factor / credit. No CoolProp
// boundary (pure planning arithmetic).

import type { PhasePowerComponentKey, ProjectPhase, ProjectIntent } from '../types/siteRag';
import { PHASE_POWER_COMPONENT_LABEL } from '../types/siteRag';
import type { TechnologyId } from './finDecision/types';
import { sourceEconomicsFor } from './finDecision/presets/shared';
import { GRID_TARIFF_BENCHMARKS } from './finDecision/presets/gridTariff';
import { effectivePhaseCapacity, effectivePhaseSourceCapacity } from './projectIntent';
import { sizingBasisLabel, resolveIntentPowerStack, resolveIntentPowerStackBand, resolvePhasePowerStack } from './powerStack';

/**
 * Map a granular phase component to its financial technology preset id.
 *
 * Returns `null` for `battery`, which is STORAGE (a buffer that shifts energy
 * in time), not a primary generator — it has no standalone capacity factor as
 * a source. Callers handle storage separately (it contributes its nameplate to
 * firm-deliverable power during its discharge window, but adds no generated
 * energy of its own to the energy tier).
 */
export function phaseComponentToTechnologyId(
  key: PhasePowerComponentKey,
): TechnologyId | null {
  switch (key) {
    case 'recip':
      return 'gas-sc';
    case 'gas-turbine':
      return 'gas-cc';
    case 'battery':
      return null; // storage, not a generator
    case 'grid':
      return 'grid-tariff';
    case 'solar-bess':
      return 'solar-bess';
    case 'wind-bess':
      return 'wind-bess';
    case 'smr':
      return 'nuclear-smr';
    case 'large-nuclear':
      return 'nuclear-large';
    default: {
      // Exhaustiveness guard — a new component key must be bridged explicitly.
      const _never: never = key;
      return _never;
    }
  }
}

/**
 * Storage availability for the battery's discharge WINDOW (percent).
 *
 * A BESS is not a generator and has no fuel-driven capacity factor; within its
 * rated discharge duration it can deliver close to nameplate. We use a 95%
 * round-trip/availability allowance (consistent with the grid-tariff
 * `availabilityPct` tier and standard 4-h BESS practice) so the battery's firm
 * contribution is its nameplate de-rated only by availability, not by a
 * generation CF. Documented ASSUMPTION — not a sourced generation figure.
 */
const BATTERY_STORAGE_AVAILABILITY_PCT = 95;

/**
 * Default capacity factor (%) per granular component key.
 *
 * Generator keys derive their CF from the sourced `*_BENCHMARKS` blocks via
 * `sourceEconomicsFor(...)` (no hardcoded CF numbers). `grid` maps to
 * `grid-tariff`, which `sourceEconomicsFor` intentionally rejects as a
 * point-source, so its CF is read directly from `GRID_TARIFF_BENCHMARKS`.
 * `battery` (storage) uses the documented availability allowance above.
 *
 * LAZY (cycle-safe): the per-key CFs are read on FIRST ACCESS, not at
 * module-evaluation time. `finDecision/presets/shared` now also reaches back
 * here (for `resolveGenerationNameplate*`), so eagerly calling
 * `sourceEconomicsFor` / `GRID_TARIFF_BENCHMARKS` at load time would race that
 * import cycle (TDZ on the benchmark bindings). A memoised builder behind a
 * `Proxy` preserves the exact `Record`-style read API (`X[key]`, spread, keys)
 * while deferring the benchmark reads until something actually indexes it.
 */
let _capacityFactorPctCache: Record<PhasePowerComponentKey, number> | null = null;
function buildDefaultCapacityFactorPct(): Record<PhasePowerComponentKey, number> {
  if (_capacityFactorPctCache) return _capacityFactorPctCache;
  _capacityFactorPctCache = {
    recip: sourceEconomicsFor('gas-sc').capacityFactorPct,
    'gas-turbine': sourceEconomicsFor('gas-cc').capacityFactorPct,
    battery: BATTERY_STORAGE_AVAILABILITY_PCT,
    grid: GRID_TARIFF_BENCHMARKS.capacityFactorPct,
    'solar-bess': sourceEconomicsFor('solar-bess').capacityFactorPct,
    'wind-bess': sourceEconomicsFor('wind-bess').capacityFactorPct,
    smr: sourceEconomicsFor('nuclear-smr').capacityFactorPct,
    'large-nuclear': sourceEconomicsFor('nuclear-large').capacityFactorPct,
  };
  return _capacityFactorPctCache;
}

export const DEFAULT_CAPACITY_FACTOR_PCT: Record<PhasePowerComponentKey, number> =
  new Proxy({} as Record<PhasePowerComponentKey, number>, {
    get(_t, prop: string) {
      return buildDefaultCapacityFactorPct()[prop as PhasePowerComponentKey];
    },
    has(_t, prop: string) {
      return prop in buildDefaultCapacityFactorPct();
    },
    ownKeys() {
      return Reflect.ownKeys(buildDefaultCapacityFactorPct());
    },
    getOwnPropertyDescriptor(_t, prop: string) {
      return {
        enumerable: true,
        configurable: true,
        value: buildDefaultCapacityFactorPct()[prop as PhasePowerComponentKey],
      };
    },
  });

/**
 * Planning reserve margin (%). A firm load is provisioned with firm capacity
 * ABOVE the peak to cover forced outages + maintenance. Resource-adequacy
 * practice (NERC 1-in-10 / 0.1 LOLE) lands at ~15–18% (e.g. CAISO 17%, SPP 12%);
 * 15% is a defensible default. "Feasible" requires firm capacity ≥ load × 1.15.
 */
export const RESERVE_MARGIN_PCT = 15;

/**
 * Capacity CREDIT (ELCC), percent of nameplate — the share you can RELY ON for
 * firm load. NOT the capacity factor (see file header).
 *
 *   Firm dispatchable (recip / gas / SMR / large nuclear): ≈ availability after
 *     forced-outage + maintenance (~92%; nuclear/grid a touch higher).
 *   grid import: ~97% (utility-grade firm interconnection / firm PPA).
 *   solar+BESS, wind+BESS: a LOW ELCC for a FLAT 24/7 load — a few hours of
 *     storage cannot firm overnight / multi-day gaps. Bare-wind ELCC ~15–17%
 *     (MISO); the ~55–60% hybrid figures (PSCo) are for PEAK-COINCIDENT loads, NOT
 *     a flat datacenter — so we use a conservative 30% and flag a full ELCC study.
 *   battery: ~95% within its rated discharge WINDOW (does not imply multi-day
 *     energy — duration-limited; see message).
 *
 * These are planning DEFAULTS, not a site-specific ELCC study. Tune per project.
 */
export const DEFAULT_CAPACITY_CREDIT_PCT: Record<PhasePowerComponentKey, number> = {
  recip: 92,
  'gas-turbine': 92,
  grid: 97,
  smr: 93,
  'large-nuclear': 93,
  'solar-bess': 30,
  'wind-bess': 30,
  battery: BATTERY_STORAGE_AVAILABILITY_PCT,
};

// ─────────────────────────────────────────────────────────────────────────────
// Generation NAMEPLATE sizing (R-FIN-POWER-STACK) — the load→nameplate building
// block. Given a FIRM 24/7 facility load (MW), how much NAMEPLATE of a single
// source must be installed to SERVE that load? Two binding constraints:
//
//   ENERGY:  nameplate × CF/100 ≥ load
//            → energyNameplate = load ÷ (CF/100)
//            (the source must, on average, generate the energy the load draws.)
//
//   FIRM:    nameplate × credit/100 ≥ load × (1 + reserve)
//            → firmNameplate = load × (1 + reserve/100) ÷ (credit/100)
//            (the source must carry the firm load PLUS the planning reserve.)
//
// We return max(energyNameplate, firmNameplate) — both must hold.
//
// Reuses the SAME CF / credit / reserve constants as the feasibility check (one
// source of truth — no re-derivation). Intermittent sources legitimately yield
// a LARGE nameplate (solar CF ~28% → ~3.6× load) because firming a flat 24/7
// load on a low-CF source requires a real overbuild — that is physical, not a
// bug. Storage ('battery') is NOT a generator and has no standalone nameplate
// as a source → returns 0.
//
// Special case 'grid': the grid supplies effectively UNLIMITED energy and is
// firm, so the energy constraint is moot — only the firm constraint binds.
// nameplate ≈ load × (1 + reserve) ÷ (credit/100), NOT load ÷ CF.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Energy-tier capacity factor (%) for SIZING a dedicated 24/7 asset.
 *
 * Intermittent sources (solar/wind) are RESOURCE-limited — their energy is
 * capped by the sun/wind, so the resource CF (`DEFAULT_CAPACITY_FACTOR_PCT`)
 * governs. Firm/dispatchable sources (recip/gas/SMR/nuclear/grid) run BASELOAD
 * at ≈ their availability (≈ capacity credit), NOT their market/peaker CF — a
 * dedicated baseload recip runs ~92% of the time, not the ~15% market CF of a
 * gas-sc peaker. Using the market CF here would absurdly oversize a baseload
 * recip ~6× (load/0.15 ≈ 6.7×). So firm sources size energy on the credit.
 */
export function baseloadEnergyCfPct(component: PhasePowerComponentKey): number {
  return classifyPhaseComponent(component) === 'intermittent'
    ? DEFAULT_CAPACITY_FACTOR_PCT[component]
    : DEFAULT_CAPACITY_CREDIT_PCT[component];
}

/**
 * Size a single source's NAMEPLATE MW to serve a firm 24/7 facility load.
 *
 * @param facilityLoadMW The firm facility load to serve (MW). ≤ 0 / non-finite → 0.
 * @param component      The generation/storage component key.
 * @returns Required nameplate MW. `battery` (storage) → 0. `grid` is firm-only
 *          (energy constraint skipped). Generators → max(energy, firm) nameplate.
 */
export function resolveGenerationNameplateMW(
  facilityLoadMW: number,
  component: PhasePowerComponentKey,
): number {
  if (!Number.isFinite(facilityLoadMW) || facilityLoadMW <= 0) return 0;

  // Storage is a buffer, not a generator — no standalone nameplate as a source.
  if (component === 'battery') return 0;

  const creditPct = DEFAULT_CAPACITY_CREDIT_PCT[component];
  // Firm constraint: carry the load PLUS the planning reserve margin.
  const firmNameplate =
    (facilityLoadMW * (1 + RESERVE_MARGIN_PCT / 100)) / (creditPct / 100);

  // Grid: unlimited firm energy — only the firm constraint binds (no load/CF).
  if (component === 'grid') return firmNameplate;

  // Energy constraint: average output must cover the load's energy draw. Firm
  // sources run baseload at ≈ availability (NOT their market/peaker CF) — see
  // baseloadEnergyCfPct (this is what stops a baseload recip oversizing ~6×).
  const energyNameplate = facilityLoadMW / (baseloadEnergyCfPct(component) / 100);

  // Both must hold — take the binding (larger) one.
  return Math.max(energyNameplate, firmNameplate);
}

/**
 * `TechnologyId`-keyed variant of {@link resolveGenerationNameplateMW}, for
 * financial pathways that key by `TechnologyId`. Maps each tech to its granular
 * component key so the SAME CF / credit / reserve source of truth is used (no
 * separate constants).
 *
 * `'grid-tariff'` → firm-only sizing. `'mixed'` is not a point-source preset
 * (no single CF/credit) → returns 0; callers size a mixed pathway component by
 * component. There is no storage-only `TechnologyId`.
 */
export function resolveGenerationNameplateForTech(
  facilityLoadMW: number,
  tech: TechnologyId,
): number {
  switch (tech) {
    case 'gas-cc':
      return resolveGenerationNameplateMW(facilityLoadMW, 'gas-turbine');
    case 'gas-sc':
      return resolveGenerationNameplateMW(facilityLoadMW, 'recip');
    case 'nuclear-smr':
      return resolveGenerationNameplateMW(facilityLoadMW, 'smr');
    case 'nuclear-large':
      return resolveGenerationNameplateMW(facilityLoadMW, 'large-nuclear');
    case 'solar-bess':
      return resolveGenerationNameplateMW(facilityLoadMW, 'solar-bess');
    case 'wind-bess':
      return resolveGenerationNameplateMW(facilityLoadMW, 'wind-bess');
    case 'grid-tariff':
      return resolveGenerationNameplateMW(facilityLoadMW, 'grid');
    case 'mixed':
      // Not a single point-source — size each mix component individually.
      return 0;
    default: {
      const _never: never = tech;
      return _never;
    }
  }
}

/* ── BTM-5 (v1.2.0) — generation land footprint ────────────────────── */

/**
 * Per-tech land footprint in acres per MW of installed GENERATION NAMEPLATE
 * (NOT per MW of IT/facility load). Footprint scales with the physical plant
 * that gets built, which for low-capacity-factor sources (solar, wind) is much
 * larger than the load it serves — `resolveGenerationNameplateForTech` already
 * resolves that oversize, so these factors are applied to the nameplate, never
 * to the raw load.
 *
 * Sources / rationale (planning-grade defaults, not a site survey):
 *   - solar-bess  ~5 ac/MW-DC — NREL "Land-Use Requirements for Solar Power
 *     Plants" (utility-scale fixed-tilt + single-axis tracking range 5-8 ac/MW;
 *     5 is the compact end with the BESS pad folded in).
 *   - wind-bess   ~0.75 ac/MW PERMANENT footprint (pads + roads + substation);
 *     the turbine ARRAY spacing spans far more land but is dual-use (grazing /
 *     crops), so permanent take is what constrains a co-located DC parcel.
 *   - gas-cc / gas-sc ~0.4 ac/MW — compact thermal block + balance-of-plant.
 *   - nuclear-smr ~1.0 ac/MW, nuclear-large ~0.9 ac/MW — reactor island +
 *     EPZ-adjacent owner-controlled area amortised over the larger blocks.
 *   - grid-tariff 0 — no on-site generation footprint (switchyard only).
 *
 * These REPLACE the prior crude `capMW × {2,1.5,6,12}` inline heuristics in
 * SiteRagReportView, which (a) keyed off the raw intent load not the nameplate
 * and (b) hard-coded different multipliers per call. Centralised here so the
 * report table, exports, and any future surface share one source of truth.
 */
export const GENERATION_LAND_ACRES_PER_NAMEPLATE_MW: Record<TechnologyId, number> = {
  'solar-bess': 5,
  'wind-bess': 0.75,
  'gas-cc': 0.4,
  'gas-sc': 0.4,
  'nuclear-smr': 1.0,
  'nuclear-large': 0.9,
  'grid-tariff': 0,
  // 'mixed' has no single nameplate; callers size per component. 0 here is a
  // safe sentinel (no footprint claimed) — see resolveGenerationLandAcresForTech.
  mixed: 0,
};

/**
 * BTM-5 (v1.2.0) — resolve the on-site generation land footprint (acres) for a
 * tech, keyed to the finDecision-sized NAMEPLATE for the given facility load
 * (not the raw load). Returns `null` when the load is unusable or the tech has
 * no single-nameplate footprint (`'mixed'`), so callers can fall back to a dash
 * rather than render a fabricated 0.
 *
 * footprint(acres) = resolveGenerationNameplateForTech(load, tech)
 *                    × GENERATION_LAND_ACRES_PER_NAMEPLATE_MW[tech]
 */
export function resolveGenerationLandAcresForTech(
  facilityLoadMW: number,
  tech: TechnologyId,
): number | null {
  if (!Number.isFinite(facilityLoadMW) || facilityLoadMW <= 0) return null;
  if (tech === 'mixed') return null;
  const nameplate = resolveGenerationNameplateForTech(facilityLoadMW, tech);
  if (!Number.isFinite(nameplate) || nameplate <= 0) return null;
  const acresPerMw = GENERATION_LAND_ACRES_PER_NAMEPLATE_MW[tech];
  const acres = nameplate * acresPerMw;
  return Number.isFinite(acres) && acres > 0 ? acres : null;
}

export type PhaseSourceClass = 'firm' | 'intermittent' | 'storage';

const FIRM_COMPONENTS: ReadonlySet<PhasePowerComponentKey> = new Set([
  'recip',
  'gas-turbine',
  'grid',
  'smr',
  'large-nuclear',
]);
const INTERMITTENT_COMPONENTS: ReadonlySet<PhasePowerComponentKey> = new Set([
  'solar-bess',
  'wind-bess',
]);

/** Classify a component as firm dispatchable, intermittent, or storage. */
export function classifyPhaseComponent(key: PhasePowerComponentKey): PhaseSourceClass {
  if (key === 'battery') return 'storage';
  if (INTERMITTENT_COMPONENTS.has(key)) return 'intermittent';
  if (FIRM_COMPONENTS.has(key)) return 'firm';
  // Unreachable given the union, but keep the classifier total.
  return 'firm';
}

export interface PhaseFeasibilityPerSource {
  component: PhasePowerComponentKey;
  /** Nameplate MW graded for this source — the user's exact figure, the
   *  range's sizing basis (midpoint), or 0 when unsized. See `capacityBasis`
   *  before quoting this as a declaration. */
  capacityMW: number;
  /** Capacity factor used for this source (%) — energy tier (avg output). */
  capacityFactorPct: number;
  /** Capacity credit / ELCC used for this source (%) — firm tier. */
  capacityCreditPct: number;
  /** Long-run average delivered MW = capacityMW × CF/100 (energy tier). */
  expectedMW: number;
  /** Firm MW you can rely on = capacityMW × credit/100 (firm tier). */
  firmMW: number;
  class: PhaseSourceClass;
  /** Which figure `capacityMW` carries — the anti-silent-collapse label:
   *  a range source is graded at its midpoint and must SAY so. */
  capacityBasis: 'declared-exact' | 'range-midpoint' | 'unsized';
  /** The declared per-source range when `capacityBasis === 'range-midpoint'`. */
  capacityRange: { min: number; max: number } | null;
}

export type PhaseFeasibilityVerdict = 'feasible' | 'marginal' | 'infeasible';

/**
 * Which load figure the verdict was graded against — the anti-silent-collapse
 * disclosure. A range-mode phase is graded at its DOCUMENTED sizing basis
 * (bucket midpoint / open-band rule), never silently at the site total:
 *   - 'declared-exact'      → the phase's own exact `capacityMW` (legacy path)
 *   - 'bucket-sizing-basis' → the phase's `capacityBucket` sizing basis
 *   - 'site-fallback'       → `siteContext.fallbackLoadMW` (no phase signal)
 *   - 'none'                → nothing to grade against (requiredMW = 0)
 */
export type PhaseFeasibilityLoadBasis =
  | 'declared-exact'
  | 'bucket-sizing-basis'
  | 'site-fallback'
  | 'none';

export interface PhaseFeasibility {
  /** Phase load to be served, MW — resolved per `loadBasis` (exact capacity,
   *  bucket sizing basis, site fallback, or 0 when nothing is declared). */
  requiredMW: number;
  /** Firm capacity required incl. reserve margin = requiredMW × (1+RM). */
  requiredFirmMW: number;
  /** Planning reserve margin applied (%). */
  reserveMarginPct: number;
  mixMode: 'or' | 'and';
  perSource: PhaseFeasibilityPerSource[];
  /** Σ expectedMW across every source (energy tier — informational). */
  totalExpectedMW: number;
  /** Σ firmMW (capacity-credit-weighted) across every source. */
  firmDeliverableMW: number;
  /** Σ expectedMW across intermittent sources only (informational). */
  intermittentExpectedMW: number;
  /** totalExpectedMW ≥ requiredMW — energy tier, informational only. */
  energyAdequate: boolean;
  /** firmDeliverableMW ≥ requiredMW (covers load, ignoring reserve). */
  firmCoversLoad: boolean;
  /** firmDeliverableMW ≥ requiredFirmMW (covers load + reserve) — the gate. */
  firmAdequate: boolean;
  verdict: PhaseFeasibilityVerdict;
  message: string;
  /** Which load figure the verdict was graded against (disclosure). */
  loadBasis: PhaseFeasibilityLoadBasis;
  /** Human-readable basis disclosure, e.g. "sizing basis 350 MW (bucket
   *  midpoint of the 200–500 MW range)". */
  loadBasisLabel: string;
  /**
   * The phase's declared load range — {mw, mw} when exact, the bucket range in
   * range mode, null when no phase-level capacity signal exists. Edges follow
   * the band convention: positive-finite or null (open bands carry 0/Infinity
   * internally; those edges surface as null here, never 0, never Infinity).
   */
  loadRange: { min: number | null; max: number | null } | null;
  /**
   * Firm adequacy re-graded at the load range's low/high edge (same firm
   * supply, edge load × (1+reserve) lifted to any declared ELCC target).
   * Null when the corresponding edge is null or there is no range. These are
   * informational convergence inputs — the VERDICT stays graded at the single
   * labeled basis above.
   */
  firmAdequateAtLoadMin: boolean | null;
  firmAdequateAtLoadMax: boolean | null;
  /**
   * Firm supply re-summed at the SOURCE ranges' low/high edges (exact sources
   * contribute their single figure at both ends), graded against
   * `requiredFirmMW`. PRESENT only when at least one source declares a range —
   * absent means every source is exact/unsized (legacy shape). Deliberately
   * one-dimensional: load edges (`firmAdequateAtLoad*`) and source edges are
   * separate seams; the 2-D convergence belongs to the recommendation engine,
   * not this grader.
   */
  sourceRangeAdequacy?: {
    firmDeliverableLowMW: number;
    firmDeliverableHighMW: number;
    firmAdequateAtLow: boolean;
    firmAdequateAtHigh: boolean;
  };
}

/** Numeric tolerance for the ≥ comparisons (MW). */
const TOL_MW = 1e-6;

function round1(mw: number): number {
  return Math.round(mw * 10) / 10;
}

/**
 * Site-level firm-capacity context the per-phase check can fall back to when a
 * phase does not carry its own `capacityMW`, threaded from the intent so the
 * declared firm-capacity basis actually grades the verdict (gaps
 * intent-power-requirement-mw / intent-firm-capacity-basis-mw /
 * intent-capacity-credit-intent).
 *
 * ALL fields optional + signal-gated — when absent the check reduces EXACTLY to
 * the legacy `phase.capacityMW`-only behaviour (the no-override invariant). The
 * fallback load is consulted ONLY when the phase declares no positive
 * `capacityMW`; the firm target raises the reserve gate ONLY when it exceeds the
 * reserve-margin requirement (a declared ELCC accreditation target is never
 * weaker than the planning reserve).
 */
export interface PhaseFeasibilitySiteContext {
  /** IT demand needs conversion to facility demand using this phase's PUE. */
  powerIntent?: ProjectIntent;
  /**
   * Firm 24/7 load to grade against when the phase carries no positive
   * `capacityMW`. Resolve from `intent.powerRequirementMW` (the exact MW load,
   * which also derives `firmCapacityBasisMW` in the Coverage Matrix). > 0 to take
   * effect.
   */
  fallbackLoadMW?: number;
  /**
   * Declared firm-capacity (ELCC) accreditation target in MW
   * (`capacityCreditIntent.targetMW`). When > 0 AND greater than the reserve-margin
   * requirement, the firm gate is raised to this target — a site that declares a
   * 500 MW firm-capacity expectation is graded against 500 MW of firm delivery,
   * not merely load × (1 + reserve). Absent / ≤ the reserve target → no effect.
   */
  firmTargetMW?: number;
}

/**
 * Assess whether a phase's power mix can serve its load.
 *
 * Returns `null` when the phase has no `powerSourceConfig` or no selected
 * components. AND mode (and — legacy quirk — an ABSENT `mixMode`) grades the
 * summed mix; OR mode grades each alternative independently and rolls the
 * per-source verdicts up. NOTE the absent-mixMode default here ('and') is the
 * OPPOSITE of the type's documented default ('or') — a known, pinned defect
 * kept until its own fix + test sweep; new callers must write `mixMode`
 * explicitly and never rely on the absent default.
 *
 * The load denominator resolves through the phase's own capacity duality
 * first (exact `capacityMW`, else the `capacityBucket` sizing basis), then
 * `siteContext.fallbackLoadMW` — see `PhaseFeasibilityLoadBasis`; the graded
 * basis is disclosed on `loadBasis`/`loadBasisLabel` and stamped into the
 * message for range-mode phases.
 *
 * `siteContext` (optional) threads the intent-declared firm-capacity basis so a
 * phase with no capacity signal still grades against the declared load, and a
 * declared ELCC `targetMW` can raise the firm gate. Absent → byte-stable with
 * the legacy phase-only behaviour.
 */
export function assessPhaseFeasibility(
  phase: ProjectPhase,
  siteContext?: PhaseFeasibilitySiteContext,
): PhaseFeasibility | null {
  const cfg = phase.powerSourceConfig;
  if (!cfg || cfg.components.length === 0) {
    return null;
  }

  // Phase load drives the verdict denominator, resolved through the phase's
  // own capacity duality (exact ⊕ range bucket) BEFORE any site-level
  // fallback. A range-mode phase is graded at its DOCUMENTED sizing basis —
  // previously it fell through to the SITE total, silently grading a
  // 200–500 MW phase against, say, a 750 MW campus figure.
  const cap = effectivePhaseCapacity(phase);
  const fallbackLoadMW =
    siteContext?.fallbackLoadMW != null &&
    Number.isFinite(siteContext.fallbackLoadMW) &&
    siteContext.fallbackLoadMW > 0
      ? siteContext.fallbackLoadMW
      : 0;
  let requiredMW: number;
  let loadBasis: PhaseFeasibilityLoadBasis;
  if (cap.isExact && cap.mw != null) {
    requiredMW = cap.mw;
    loadBasis = 'declared-exact';
  } else if (cap.sizingBasisMW != null && cap.sizingBasisMW > 0) {
    requiredMW = cap.sizingBasisMW;
    loadBasis = 'bucket-sizing-basis';
  } else if (fallbackLoadMW > 0) {
    requiredMW = fallbackLoadMW;
    loadBasis = 'site-fallback';
  } else {
    requiredMW = 0;
    loadBasis = 'none';
  }
  const powerIntent = siteContext?.powerIntent;
  const facilityMultiplier = powerIntent?.powerBasis === 'it-load' && loadBasis !== 'site-fallback'
    ? resolvePhasePowerStack({ ...phase, capacityMW: 1 }, powerIntent).pue
    : 1;
  requiredMW *= facilityMultiplier;
  // Band edge convention: positive-finite or null — open bands carry 0/Infinity
  // internally and those edges must never surface as literal 0 / Infinity.
  const positiveFiniteOrNull = (v: number | null | undefined): number | null =>
    typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;
  const loadRange =
    cap.range == null
      ? null
      : { min: positiveFiniteOrNull(cap.range.min * facilityMultiplier), max: positiveFiniteOrNull(cap.range.max * facilityMultiplier) };
  const rawLoadBasisLabel =
    loadBasis === 'declared-exact'
      ? `declared ${round1(requiredMW)} MW`
      : loadBasis === 'bucket-sizing-basis'
        ? `sizing basis ${round1(requiredMW)} MW (${
            cap.sizingBasisRule ? sizingBasisLabel(cap.sizingBasisRule) : 'bucket basis'
          } of the ${cap.displayLabel} range)`
        : loadBasis === 'site-fallback'
          ? `site-level fallback ${round1(requiredMW)} MW`
          : 'no load declared';
  const loadBasisLabel = facilityMultiplier !== 1
    ? `${rawLoadBasisLabel} facility demand (IT load × PUE ${facilityMultiplier})`
    : rawLoadBasisLabel;
  // Reserve-margin firm requirement, then lift to the declared ELCC firm target
  // when it is higher (a declared accreditation target binds at least as hard as
  // the planning reserve). targetMW ≤ reserve requirement → no change.
  const reserveFirmMW = requiredMW * (1 + RESERVE_MARGIN_PCT / 100);
  const firmTargetMW =
    siteContext?.firmTargetMW != null &&
    Number.isFinite(siteContext.firmTargetMW) &&
    siteContext.firmTargetMW > 0
      ? siteContext.firmTargetMW
      : 0;
  const requiredFirmMW = Math.max(reserveFirmMW, firmTargetMW);
  const params = cfg.componentParams ?? {};

  const perSource: PhaseFeasibilityPerSource[] = cfg.components.map(component => {
    const p = params[component];
    // Per-source capacity duality (exact ⊕ free range) — a range source is
    // graded at its labeled sizing basis (midpoint), never silently collapsed.
    const sourceCap = effectivePhaseSourceCapacity(phase, component);
    const capacityMW = sourceCap.sizingBasisMW ?? 0;
    const capacityFactorPct = p?.capacityFactorPct ?? DEFAULT_CAPACITY_FACTOR_PCT[component];
    const capacityCreditPct = DEFAULT_CAPACITY_CREDIT_PCT[component];
    const cls = classifyPhaseComponent(component);
    return {
      component,
      capacityMW,
      capacityFactorPct,
      capacityCreditPct,
      // Energy tier: storage adds no generated energy of its own; everything else
      // contributes its long-run average output.
      expectedMW: cls === 'storage' ? 0 : capacityMW * (capacityFactorPct / 100),
      // Firm tier: capacity-credit-weighted MW you can rely on. Firm dispatchable
      // → ~availability; intermittent → low ELCC; storage → availability within
      // its discharge window. THIS, not CF, drives the verdict.
      firmMW: capacityMW * (capacityCreditPct / 100),
      class: cls,
      capacityBasis:
        sourceCap.kind === 'exact'
          ? 'declared-exact'
          : sourceCap.kind === 'range'
            ? 'range-midpoint'
            : 'unsized',
      capacityRange:
        sourceCap.kind === 'range' && sourceCap.minMW != null && sourceCap.maxMW != null
          ? { min: sourceCap.minMW, max: sourceCap.maxMW }
          : null,
    };
  });

  let totalExpectedMW = 0;
  let intermittentExpectedMW = 0;
  let firmDeliverableMW = 0;

  const isAndMode = cfg.mixMode === 'and' || cfg.mixMode === undefined;

  if (isAndMode) {
    totalExpectedMW = perSource.reduce((sum, s) => sum + s.expectedMW, 0);
    intermittentExpectedMW = perSource
      .filter(s => s.class === 'intermittent')
      .reduce((sum, s) => sum + s.expectedMW, 0);
    firmDeliverableMW = perSource.reduce((sum, s) => sum + s.firmMW, 0);
  } else {
    totalExpectedMW = perSource.length > 0 ? Math.max(...perSource.map(s => s.expectedMW)) : 0;
    const intermittent = perSource.filter(s => s.class === 'intermittent');
    intermittentExpectedMW = intermittent.length > 0 ? Math.max(...intermittent.map(s => s.expectedMW)) : 0;
    firmDeliverableMW = perSource.length > 0 ? Math.max(...perSource.map(s => s.firmMW)) : 0;
  }

  // Firm supply re-summed at the SOURCE ranges' edges — presence-gated on any
  // range-mode source. Exact sources contribute their single firmMW at both
  // ends; AND sums, OR maxes (mirroring the headline aggregation above).
  const hasRangeSource = perSource.some(s => s.capacityBasis === 'range-midpoint');
  const sourceRangeAdequacy = (() => {
    if (!hasRangeSource) return undefined;
    const firmAtEdge = (s: PhaseFeasibilityPerSource, edge: 'min' | 'max'): number =>
      (s.capacityRange ? s.capacityRange[edge] : s.capacityMW) * (s.capacityCreditPct / 100);
    const lows = perSource.map(s => firmAtEdge(s, 'min'));
    const highs = perSource.map(s => firmAtEdge(s, 'max'));
    const firmDeliverableLowMW = isAndMode
      ? lows.reduce((sum, v) => sum + v, 0)
      : lows.length > 0 ? Math.max(...lows) : 0;
    const firmDeliverableHighMW = isAndMode
      ? highs.reduce((sum, v) => sum + v, 0)
      : highs.length > 0 ? Math.max(...highs) : 0;
    return {
      firmDeliverableLowMW,
      firmDeliverableHighMW,
      firmAdequateAtLow: firmDeliverableLowMW + TOL_MW >= requiredFirmMW,
      firmAdequateAtHigh: firmDeliverableHighMW + TOL_MW >= requiredFirmMW,
    };
  })();

  // Firm adequacy re-graded at the load range's edges — informational
  // convergence inputs for range-mode phases; the verdict itself stays graded
  // at the single labeled sizing basis.
  const firmAdequateAtEdge = (edge: number | null): boolean | null =>
    edge == null
      ? null
      : firmDeliverableMW + TOL_MW >=
        Math.max(edge * (1 + RESERVE_MARGIN_PCT / 100), firmTargetMW);
  const firmAdequateAtLoadMin = loadRange ? firmAdequateAtEdge(loadRange.min) : null;
  const firmAdequateAtLoadMax = loadRange ? firmAdequateAtEdge(loadRange.max) : null;

  const gradeAdequacy = (expected: number, firm: number) => {
    const energyAdeq = expected + TOL_MW >= requiredMW;
    const firmCovers = firm + TOL_MW >= requiredMW;
    const firmAdeq = firm + TOL_MW >= requiredFirmMW;

    let v: PhaseFeasibilityVerdict;
    if (firmAdeq && energyAdeq) {
      v = 'feasible';
    } else if (firmCovers && energyAdeq) {
      v = 'marginal';
    } else {
      v = 'infeasible';
    }
    return { energyAdequate: energyAdeq, firmCoversLoad: firmCovers, firmAdequate: firmAdeq, verdict: v };
  };

  let energyAdequate: boolean;
  let firmCoversLoad: boolean;
  let firmAdequate: boolean;
  let verdict: PhaseFeasibilityVerdict;
  let message: string;

  if (isAndMode) {
    const grade = gradeAdequacy(totalExpectedMW, firmDeliverableMW);
    energyAdequate = grade.energyAdequate;
    firmCoversLoad = grade.firmCoversLoad;
    firmAdequate = grade.firmAdequate;
    verdict = grade.verdict;

    if (verdict === 'feasible') {
      message =
        `Feasible: firm ${round1(firmDeliverableMW)} MW covers the ${round1(requiredMW)} MW load ` +
        `with the ${RESERVE_MARGIN_PCT}% reserve margin (${round1(requiredFirmMW)} MW firm needed)`;
    } else if (verdict === 'marginal') {
      const shortMW = round1(requiredFirmMW - firmDeliverableMW);
      message =
        `Marginal: firm ${round1(firmDeliverableMW)} MW covers the ${round1(requiredMW)} MW load ` +
        `but is ${shortMW} MW short of the ${RESERVE_MARGIN_PCT}% reserve margin — add firm headroom`;
    } else {
      if (!grade.firmCoversLoad || !grade.firmAdequate && grade.energyAdequate) {
        const shortMW = round1(requiredMW - firmDeliverableMW);
        const unsized = perSource.filter(s => s.capacityMW <= 0);
        const unsizedNames = unsized.map(s => PHASE_POWER_COMPONENT_LABEL[s.component]).join(', ');
        
        if (unsized.length > 0 && firmDeliverableMW <= TOL_MW) {
          message =
            `Infeasible: enter capacity for the selected source(s) — ${unsizedNames} ` +
            `${unsized.length === 1 ? 'is' : 'are'} set to 0 MW, so the mix delivers 0 firm MW ` +
            `against the ${round1(requiredMW)} MW load`;
        } else if (unsized.length > 0) {
          const intermittentNote = intermittentExpectedMW > TOL_MW
            ? ' — intermittent sources carry little firm credit on a 24/7 load'
            : '';
          const shortFirmMW = round1(requiredFirmMW - firmDeliverableMW);
          message =
            `Infeasible: firm ${round1(firmDeliverableMW)} MW vs ${round1(requiredMW)} MW target load ` +
            `(${round1(requiredFirmMW)} MW required firm at ${RESERVE_MARGIN_PCT}% reserve), ` +
            `short ${shortMW > 0 ? `${shortMW} MW vs target load (${shortFirmMW} MW short vs required firm)` : `${shortFirmMW} MW vs required firm`}${intermittentNote} (${unsizedNames} ` +
            `${unsized.length === 1 ? 'is' : 'are'} set to 0 MW — enter ${unsized.length === 1 ? 'its' : 'their'} capacity)`;
        } else {
          const intermittentNote = intermittentExpectedMW > TOL_MW
            ? ' — intermittent sources carry little firm credit on a 24/7 load; add firm capacity or storage'
            : ' — add firm capacity';
          const shortFirmMW = round1(requiredFirmMW - firmDeliverableMW);
          message =
            `Infeasible: firm ${round1(firmDeliverableMW)} MW vs ${round1(requiredMW)} MW target load ` +
            `(${round1(requiredFirmMW)} MW required firm at ${RESERVE_MARGIN_PCT}% reserve), ` +
            `short ${shortMW > 0 ? `${shortMW} MW vs target load (${shortFirmMW} MW short vs required firm)` : `${shortFirmMW} MW vs required firm`}${intermittentNote}`;
        }
      } else {
        const shortEnergyMW = round1(requiredMW - totalExpectedMW);
        message = `Infeasible: mix delivers sufficient firm capacity, but average energy output (${round1(totalExpectedMW)} MW) is ${shortEnergyMW} MW short of the 24/7 ${round1(requiredMW)} MW load. Increase Capacity Factor (CF) or nameplate.`;
      }
    }
  } else {
    const grades = perSource.map(s => ({
      source: s,
      grade: gradeAdequacy(s.expectedMW, s.firmMW),
    }));

    const allFeasible = grades.every(g => g.grade.verdict === 'feasible');
    const anyFeasible = grades.some(g => g.grade.verdict === 'feasible');

    if (allFeasible) {
      verdict = 'feasible';
      message = `Feasible: all ${grades.length} alternative sources independently cover the ${round1(requiredMW)} MW load with reserve.`;
    } else if (anyFeasible) {
      verdict = 'marginal';
      const feasibleNames = grades.filter(g => g.grade.verdict === 'feasible').map(g => PHASE_POWER_COMPONENT_LABEL[g.source.component]);
      const shortNames = grades.filter(g => g.grade.verdict !== 'feasible').map(g => PHASE_POWER_COMPONENT_LABEL[g.source.component]);
      message = `Mixed: ${feasibleNames.join(', ')} can independently cover the load, but ${shortNames.join(', ')} ${shortNames.length === 1 ? 'is' : 'are'} short.`;
    } else {
      verdict = 'infeasible';
      const unsized = perSource.filter(s => s.capacityMW <= 0);
      if (unsized.length === perSource.length) {
        message = `Infeasible: enter capacity for the selected alternative sources — all are set to 0 MW.`;
      } else {
        message = `Infeasible: none of the alternative sources can independently cover the ${round1(requiredMW)} MW load.`;
      }
    }

    const bestGrade = grades.reduce((best, curr) => {
      if (curr.grade.verdict === 'feasible') return curr;
      if (best.grade.verdict === 'feasible') return best;
      if (curr.grade.verdict === 'marginal') return curr;
      return best;
    }, grades[0]);

    energyAdequate = bestGrade.grade.energyAdequate;
    firmCoversLoad = bestGrade.grade.firmCoversLoad;
    firmAdequate = bestGrade.grade.firmAdequate;
  }

  // A verdict graded at a range's sizing basis must SAY so wherever the
  // message travels — the basis clause rides on the message itself so no
  // downstream surface can quote the verdict without its basis.
  if (loadBasis === 'bucket-sizing-basis') {
    message += ` — graded at the ${round1(requiredMW)} MW sizing basis of the ${cap.displayLabel} range, not an exact declaration`;
  }

  return {
    requiredMW,
    requiredFirmMW,
    reserveMarginPct: RESERVE_MARGIN_PCT,
    mixMode: cfg.mixMode ?? 'and',
    perSource,
    totalExpectedMW,
    firmDeliverableMW,
    intermittentExpectedMW,
    energyAdequate,
    firmCoversLoad,
    firmAdequate,
    verdict,
    message,
    loadBasis,
    loadBasisLabel,
    loadRange,
    firmAdequateAtLoadMin,
    firmAdequateAtLoadMax,
    ...(sourceRangeAdequacy ? { sourceRangeAdequacy } : {}),
  };
}

/**
 * Roll the per-phase feasibility checks up to a single worst-case summary for a
 * site — used to WARN on the Site Overview + Dashboard (it does NOT feed the
 * portfolio composite score; feasibility is an advisory overlay, not a scored
 * input). Configured AND mixes and OR alternatives are assessed; unconfigured
 * phases are skipped. Equal verdicts retain the first phase in input order.
 */
export interface PhaseFeasibilitySummary {
  /** Worst verdict across configured phases, or null if none were assessable. */
  worstVerdict: PhaseFeasibilityVerdict | null;
  infeasibleCount: number;
  marginalCount: number;
  assessedCount: number;
  /** Worst-offending phase's message + label, for a one-line warning. */
  worstMessage: string | null;
  worstPhaseLabel: string | null;
  /** The SAME assessment selected by the worst-verdict policy. Never join by label. */
  worstPhaseAssessment?: {
    phaseId: string;
    phaseLabel: string;
    assessment: PhaseFeasibility;
  } | null;
}

const VERDICT_RANK: Record<PhaseFeasibilityVerdict, number> = {
  feasible: 0,
  marginal: 1,
  infeasible: 2,
};

export function summarizePhaseFeasibility(
  phases: ReadonlyArray<ProjectPhase> | undefined | null,
  siteContext?: PhaseFeasibilitySiteContext,
): PhaseFeasibilitySummary {
  const summary: PhaseFeasibilitySummary = {
    worstVerdict: null,
    infeasibleCount: 0,
    marginalCount: 0,
    assessedCount: 0,
    worstMessage: null,
    worstPhaseLabel: null,
    worstPhaseAssessment: null,
  };
  if (!phases || phases.length === 0) return summary;

  for (const phase of phases) {
    const f = assessPhaseFeasibility(phase, siteContext);
    if (!f) continue;
    summary.assessedCount += 1;
    if (f.verdict === 'infeasible') summary.infeasibleCount += 1;
    else if (f.verdict === 'marginal') summary.marginalCount += 1;
    if (
      summary.worstVerdict === null ||
      VERDICT_RANK[f.verdict] > VERDICT_RANK[summary.worstVerdict]
    ) {
      summary.worstVerdict = f.verdict;
      summary.worstMessage = f.message;
      summary.worstPhaseLabel = phase.label;
      summary.worstPhaseAssessment = {
        phaseId: phase.id,
        phaseLabel: phase.label,
        assessment: f,
      };
    }
  }
  return summary;
}

/**
 * Resolve the site-level firm-capacity context from an intent — the SSOT for
 * the firm-capacity basis the per-phase check falls back to. Pure + signal-gated:
 *   - `fallbackLoadMW` from `intent.powerRequirementMW` (the exact MW load; the
 *     same figure that derives `firmCapacityBasisMW` in the Coverage Matrix).
 *   - `firmTargetMW` from `intent.capacityCreditIntent.targetMW` (the declared
 *     ELCC accreditation target), consulted only when `target !== 'none'`.
 * Both omitted when their source is absent / non-positive → byte-stable.
 */
export function firmCapacitySiteContextFromIntent(
  intent: ProjectIntent | undefined | null,
): PhaseFeasibilitySiteContext | undefined {
  if (!intent) return undefined;
  // A phase with no load falls back to the SITE declaration, not to another
  // phase's capacity. Convert that fallback once before passing it to grading.
  const siteIntent = { ...intent, phases: undefined };
  const loadMW = resolveIntentPowerStack(siteIntent).facilityMW
    ?? resolveIntentPowerStackBand(siteIntent)?.stacks.sizingBasis.facilityMW;
  const fallbackLoadMW =
    typeof loadMW === 'number' && Number.isFinite(loadMW) && loadMW > 0
      ? loadMW
      : undefined;
  const cci = intent.capacityCreditIntent;
  const targetMW =
    cci && cci.target !== 'none' &&
    typeof cci.targetMW === 'number' &&
    Number.isFinite(cci.targetMW) &&
    cci.targetMW > 0
      ? cci.targetMW
      : undefined;
  if (fallbackLoadMW === undefined && targetMW === undefined && intent.powerBasis !== 'it-load') return undefined;
  return {
    ...(intent.powerBasis === 'it-load' ? { powerIntent: intent } : {}),
    ...(fallbackLoadMW !== undefined ? { fallbackLoadMW } : {}),
    ...(targetMW !== undefined ? { firmTargetMW: targetMW } : {}),
  };
}

/**
 * Convenience: worst-case feasibility for a whole intent's phases. Threads the
 * intent-declared firm-capacity basis (powerRequirementMW / capacityCreditIntent
 * targetMW) so the declared load + ELCC target actually grade the verdict, not
 * merely the per-phase `capacityMW`.
 */
export function summarizeIntentFeasibility(
  intent: ProjectIntent | undefined | null,
): PhaseFeasibilitySummary {
  return summarizePhaseFeasibility(
    intent?.phases,
    firmCapacitySiteContextFromIntent(intent),
  );
}
